import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Plus, Search, Trash2, Send, ChevronDown, ChevronUp, Pencil, Ban } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { PromoOverlapResolutionDialog } from '../components/promotions/PromoOverlapResolutionDialog';
import { PromoPlatformTasks } from '../components/promotions/PromoPlatformTasks';
import { PromoOperationsAuditPanel } from '../components/promotions/PromoOperationsAuditPanel';

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtD = (s) => { if (!s) return ''; const d = new Date(s); return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear().toString().slice(-2)}`; };
const fmtDT = (s) => { if (!s) return ''; return new Date(s).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); };
const STATUS_BADGE = {
  attiva: 'bg-emerald-100 text-emerald-700', futura: 'bg-blue-100 text-blue-700', conclusa: 'bg-slate-200 text-slate-600',
  cessata: 'bg-rose-100 text-rose-700',
};
const REQ_BADGE = {
  pending: 'bg-amber-100 text-amber-700', owner_blocked: 'bg-rose-600 text-white',
  approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-slate-200 text-slate-600',
};
const REQ_LABEL = { pending: 'IN ATTESA', owner_blocked: 'BLOCCATA DAL PROPRIETARIO', approved: 'APPROVATA', rejected: 'RIFIUTATA' };
const ORIGIN_LABEL = {
  partner_hwacademy: ['PARTNER HWA', 'bg-violet-100 text-violet-700'],
  partner: ['PARTNER', 'bg-violet-50 text-violet-600'],
  proprietario_email: ['PROPR. EMAIL', 'bg-sky-50 text-sky-700'],
  proprietario_telefono: ['PROPR. TEL', 'bg-sky-50 text-sky-700'],
  ufficio: ['UFFICIO', 'bg-slate-100 text-slate-500'],
};

function PartnerRequestsTable({ data, reload, onChanged }) {
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(null);
  const [overlap, setOverlap] = useState(null);
  const reqs = data?.requests || [];

  const approve = async (r, overlapResolution = null) => {
    setBusy(r.id);
    try {
      await api.post(`/promotions/partner-requests/${r.id}/approve`, overlapResolution ? { overlap_resolution: overlapResolution } : {});
      toast.success('Promozione ATTIVATA — attività piattaforme disponibili in dashboard');
      setOverlap(null); reload(); onChanged();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.code === 'promotion_checkin_overlap') {
        setOverlap({ request: r, detail: e.response.data.detail });
      } else toast.error(e.response?.data?.detail || 'Errore');
    } finally { setBusy(null); }
  };

  const decide = async (r, action) => {
    let body = {};
    if (action === 'reject') {
      const reason = window.prompt('Motivo del rifiuto (il partner lo vedrà su HW Academy):');
      if (reason === null) return;
      body = { reason };
    } else if (!window.confirm(r.type === 'cancel'
      ? `Confermare l'ANNULLAMENTO della promozione -${r.pct}% su ${r.property_name}?`
      : `ATTIVARE la promozione -${r.pct}% su ${r.property_name}?\n\nPartiranno le email di attivazione a proprietario e partner.`)) return;
    if (action === 'approve' && r.type === 'create') return approve(r);
    setBusy(r.id);
    try {
      await api.post(`/promotions/partner-requests/${r.id}/${action}`, body);
      toast.success(action === 'reject' ? 'Richiesta rifiutata'
        : r.type === 'cancel' ? 'Promozione cessata da oggi' : 'Promozione ATTIVATA — email di attivazione inviate a proprietario e partner');
      reload(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setBusy(null); }
  };

  const open = reqs.filter(r => ['pending', 'owner_blocked'].includes(r.status));
  const history = reqs.filter(r => !['pending', 'owner_blocked'].includes(r.status));

  const Row = ({ r, decided }) => (
    <tr className={`border-b border-slate-100 ${r.status === 'owner_blocked' ? 'bg-rose-50' : ''}`} data-testid={`partner-req-${r.id}`}>
      <td className="py-2 pr-2">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.type === 'cancel' ? 'bg-rose-100 text-rose-700' : 'bg-violet-100 text-violet-700'}`}>
          {r.type === 'cancel' ? 'ANNULLAMENTO' : 'NUOVA PROMO'}
        </span>
        {r.renewal && <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">RINNOVO</span>}
      </td>
      <td className="py-2 pr-2"><span className="font-mono font-semibold">{r.property_code}</span> <span className="text-slate-500 text-xs">{(r.property_name || '').slice(0, 32)}</span></td>
      <td className="py-2 pr-2 text-center font-bold text-blue-700">-{r.pct}%</td>
      <td className="py-2 pr-2 text-xs">{fmtD(r.stay_from)} → {fmtD(r.stay_to)}</td>
      <td className="py-2 pr-2 text-xs">{fmtD(r.validity_from)} → {fmtD(r.validity_to)}</td>
      <td className="py-2 pr-2 text-xs">
        {r.requested_by_role === 'owner'
          ? <span className="font-semibold text-emerald-700" data-testid={`req-by-owner-${r.id}`}>👤 Proprietario {r.owner_name || ''}</span>
          : (r.partner_name || r.partner_code)}
      </td>
      <td className="py-2 pr-2 text-center">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${REQ_BADGE[r.status]}`}>{REQ_LABEL[r.status]}</span>
        {r.status === 'pending' && !r.can_approve && (
          <div className="text-[10px] text-amber-600 mt-0.5" data-testid={`partner-req-wait-${r.id}`}>⏳ proprietario avvisato — lavorabile dal {fmtDT(r.workable_from)}</div>)}
        {r.status === 'pending' && r.can_approve && (
          <div className="text-[10px] text-emerald-600 font-semibold mt-0.5" data-testid={`partner-req-ready-${r.id}`}>✓ pronta da attivare</div>)}
        {r.status === 'pending' && !r.owner_email_sent && (
          <div className="text-[10px] text-rose-600 mt-0.5">⚠ proprietario senza email: verifica manuale</div>)}
        {r.status === 'rejected' && r.reject_reason && <div className="text-[10px] text-slate-400 mt-0.5">{r.reject_reason}</div>}
      </td>
      <td className="py-2 whitespace-nowrap text-right">
        {!decided && r.status === 'pending' && (
          <Button size="sm" className="mr-2 h-7 bg-emerald-600 hover:bg-emerald-700" disabled={!r.can_approve || busy === r.id}
            title={r.can_approve ? 'Attiva la promozione' : 'In attesa delle 24 ore dalla mail al proprietario'}
            onClick={() => decide(r, 'approve')} data-testid={`partner-req-approve-${r.id}`}>
            {busy === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : (r.type === 'cancel' ? 'Conferma annullo' : '✓ Attiva')}
          </Button>)}
        {!decided && (
          <Button size="sm" variant="outline" className="h-7 text-rose-600 border-rose-200" disabled={busy === r.id}
            onClick={() => decide(r, 'reject')} data-testid={`partner-req-reject-${r.id}`}>Rifiuta</Button>)}
        {decided && <span className="text-[10px] text-slate-400">{fmtD(r.decided_at)} · {r.decided_by}</span>}
      </td>
    </tr>
  );

  return (
    <div data-testid="partner-requests-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h2 className="text-sm font-bold text-slate-700">📨 Richieste dei partner (HW Academy) e promo in attesa di attivazione
          {open.length > 0 && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold" data-testid="partner-req-pending-count">{open.length} da lavorare</span>}
        </h2>
        {history.length > 0 && (
          <button onClick={() => setShowHistory(v => !v)} className="text-xs text-blue-600" data-testid="partner-req-history-toggle">
            {showHistory ? 'Nascondi storico' : `Storico (${history.length})`}
          </button>)}
      </div>
      {!open.length && !showHistory ? <p className="py-8 text-center text-sm text-slate-400">Nessuna promozione da attivare. Le richieste dei partner da HW Academy compariranno qui.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b text-xs">
              <th className="py-2 pr-2">TIPO</th><th>IMMOBILE</th><th className="text-center">%</th>
              <th>SOGGIORNI</th><th>VALIDITÀ</th><th>PARTNER</th><th className="text-center">STATO</th><th></th>
            </tr></thead>
            <tbody>
              {open.map(r => <Row key={r.id} r={r} decided={false} />)}
              {showHistory && history.map(r => <Row key={r.id} r={r} decided={true} />)}
            </tbody>
          </table>
        </div>
      )}
      <PromoOverlapResolutionDialog open={!!overlap} conflict={overlap?.detail} busy={busy === overlap?.request?.id}
        onCancel={() => setOverlap(null)} onConfirm={(choice) => approve(overlap.request, choice)} />
    </div>
  );
}

function PromoForm({ open, onClose, properties, isAdmin, editing, onSaved }) {
  const empty = { property_id: '', pct: '', validity_from: '', validity_to: '', stay_from: '', stay_to: '', no_end: false, cumulative: false, created_date: '', origin: 'proprietario_email' };
  const [f, setF] = useState(empty);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [overlap, setOverlap] = useState(null);
  useEffect(() => {
    if (editing) setF({ ...editing, created_date: editing.created_at, no_end: !!editing.no_end,
      origin: editing.origin || 'proprietario_email',
      validity_to: editing.no_end ? '' : editing.validity_to, stay_to: editing.no_end ? '' : editing.stay_to });
    else setF(empty);
    setQ('');
  }, [editing, open]); // eslint-disable-line
  const filtered = useMemo(() => properties.filter(p =>
    !q || `${p.name} ${p.property_code} ${p.city}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40), [properties, q]);
  const save = async (overlapResolution = null) => {
    if (!f.property_id || !f.pct || !f.validity_from || !f.stay_from || (!f.no_end && !f.stay_to))
      return toast.error(f.no_end ? 'Compila immobile, % e le date di inizio' : 'Compila immobile, %, date di inizio e fine soggiorni');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/promotions/${editing.id}`, f);
        toast.success('Promozione aggiornata');
      } else {
        const res = await api.post('/promotions', overlapResolution ? { ...f, overlap_resolution: overlapResolution } : f);
        toast.success(res.data.email_sent ? 'Promozione creata — email inviate a proprietario e partner'
          : 'Promozione creata (nessuna email)');
      }
      setOverlap(null);
      onSaved();
      onClose();
    } catch (e) {
      if (e.response?.status === 409 && e.response?.data?.detail?.code === 'promotion_checkin_overlap') {
        setOverlap(e.response.data.detail);
      } else toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };
  const selProp = properties.find(p => p.id === f.property_id);
  return (<>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="promo-form-dialog">
        <DialogHeader><DialogTitle>{editing ? 'Modifica promozione' : 'Nuova promozione'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!editing && (
            <div>
              <label className="text-xs font-semibold text-slate-500">Immobile</label>
              {selProp ? (
                <div className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm bg-blue-50">
                  <span><b>{selProp.property_code}</b> · {selProp.name}</span>
                  <button className="text-xs text-blue-600" onClick={() => setF({ ...f, property_id: '' })}>cambia</button>
                </div>
              ) : (
                <>
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca per nome o codice…"
                    className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="promo-prop-search" />
                  <div className="max-h-40 overflow-y-auto border rounded-lg mt-1 divide-y">
                    {filtered.map(p => (
                      <button key={p.id} onClick={() => setF({ ...f, property_id: p.id })}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50"
                        data-testid={`promo-prop-opt-${p.property_code}`}>
                        <b>{p.property_code}</b> · {p.name} <span className="text-slate-400 text-xs">{p.city}</span>
                        {!p.owner_email && <span className="ml-1 text-[10px] text-amber-600">(proprietario senza email)</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Sconto %</label>
              <input type="number" min="1" max="90" value={f.pct} onChange={e => setF({ ...f, pct: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="promo-pct" />
            </div>
            {isAdmin && (
              <div>
                <label className="text-xs font-semibold text-slate-500">Data creazione (retroattiva)</label>
                <input type="date" value={f.created_date || ''} onChange={e => setF({ ...f, created_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="promo-created-date" />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Richiesta da (provenienza — passata anche a HW Academy)</label>
            <select value={f.origin || 'proprietario_email'} onChange={e => setF({ ...f, origin: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="promo-origin">
              <option value="proprietario_email">Proprietario — richiesta via email</option>
              <option value="proprietario_telefono">Proprietario — richiesta telefonica</option>
              <option value="partner">Partner (fuori da HW Academy)</option>
              <option value="ufficio">Iniziativa ufficio</option>
            </select>
          </div>
          {editing && <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={!!f.cumulative} onChange={e => setF({ ...f, cumulative: e.target.checked })}
              className="h-4 w-4 accent-blue-600" data-testid="promo-cumulative" />
            <span><b>Si somma</b> — cumulabile con le altre promozioni della stessa casa (gli sconti si sommano)</span>
          </label>}
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={!!f.no_end} onChange={e => setF({ ...f, no_end: e.target.checked })}
              className="h-4 w-4 accent-blue-600" data-testid="promo-no-end" />
            <span><b>Senza scadenza</b> — la promozione resta sempre attiva finché non la elimini</span>
          </label>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Validità — le prenotazioni devono ARRIVARE in questo periodo</p>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={f.validity_from} onChange={e => setF({ ...f, validity_from: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="promo-validity-from" />
              {f.no_end ? <div className="border rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-400">∞ senza scadenza</div>
                : <input type="date" value={f.validity_to} onChange={e => setF({ ...f, validity_to: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="promo-validity-to" />}
            </div>
            {!f.no_end && <p className="text-[11px] text-slate-400 mt-1">La scadenza può essere anticipata rispetto ai soggiorni — se la lasci vuota finisce automaticamente con la fine dei soggiorni</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-1">Soggiorni promozionati — dal / al</p>
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={f.stay_from} onChange={e => setF({ ...f, stay_from: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="promo-stay-from" />
              {f.no_end ? <div className="border rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-400">∞ senza scadenza</div>
                : <input type="date" value={f.stay_to} onChange={e => setF({ ...f, stay_to: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="promo-stay-to" />}
            </div>
          </div>
          {isAdmin ? <p className="text-[11px] text-amber-600">Promo creata da admin: nessuna email a proprietario/partner (né alla creazione né a fine promo)</p>
            : <p className="text-[11px] text-blue-600">Alla creazione partiranno le email a proprietario e partner di riferimento (admin in copia)</p>}
          <Button onClick={() => save()} disabled={saving} className="w-full" data-testid="promo-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {editing ? 'Salva modifiche' : 'Crea promozione'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    <PromoOverlapResolutionDialog open={!!overlap} conflict={overlap} busy={saving}
      onCancel={() => setOverlap(null)} onConfirm={(choice) => save(choice)} />
  </>);
}

function PromoDetail({ promo, isAdmin }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/promotions/${promo.id}/bookings`).then(r => setData(r.data)).catch(() => {});
  }, [promo.id]);
  if (!data) return <div className="p-3 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />carico…</div>;
  if (!data.bookings.length) return <p className="p-3 text-xs text-slate-400">Nessuna prenotazione in promozione (per ora).</p>;
  return (
    <div className="p-3 bg-slate-50">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500 border-b">
          <th className="py-1">Prenotazione</th><th>Arrivata</th><th>Soggiorno</th>
          <th className="text-center">Notti promo</th><th className="text-right">Al proprietario (scontato)</th>
          {isAdmin && <th className="text-right">Incassato HW</th>}
        </tr></thead>
        <tbody>{data.bookings.map((b, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="py-1 font-mono">{b.booking_code}</td>
            <td>{fmtD(b.arrived)}</td>
            <td>{fmtD(b.check_in)} → {fmtD(b.check_out)}</td>
            <td className="text-center">{b.nights_promo}{b.nights_promo < b.nights_total ? `/${b.nights_total}` : ''}</td>
            <td className="text-right font-semibold">{eur(b.owner_discounted)} <span className="text-slate-400 line-through">{eur(b.owner_amount)}</span></td>
            {isAdmin && <td className="text-right font-semibold text-emerald-700" data-testid={`promo-hw-collected-${b.booking_code}`}>{eur(b.hw_collected)}</td>}
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export default function PromozioniPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('tab');
    return ['piattaforme', 'da-attivare', 'attive', 'terminate'].includes(requested) ? requested : 'attive';
  });
  const [reqData, setReqData] = useState(null);
  const loadReqs = useCallback(() => {
    api.get('/promotions/partner-requests').then(r => setReqData(r.data)).catch(() => {});
  }, []);
  const [taskData, setTaskData] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const loadTasks = useCallback(() => {
    setTasksLoading(true);
    api.get('/promotions/publication-tasks').then(r => setTaskData(r.data)).catch(() => toast.error('Errore attività piattaforme')).finally(() => setTasksLoading(false));
  }, []);
  const [sort, setSort] = useState({ key: 'created_at', dir: -1 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null);
  const [totals, setTotals] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/promotions').then(r => { setRows(r.data.promotions); setTotals(r.data.totals || null); }).catch(() => toast.error('Errore caricamento')).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    load();
    loadReqs();
    loadTasks();
    api.get('/promotions/properties-list').then(r => setProps(r.data.properties)).catch(() => {});
  }, [load, loadReqs, loadTasks]);

  const shown = useMemo(() => {
    const ended = (r) => r.status === 'conclusa' || r.status === 'cessata';
    let out = rows.filter(r =>
      (tab === 'attive' ? !ended(r) : ended(r)) &&
      (!search || `${r.property_name} ${r.property_code}`.toLowerCase().includes(search.toLowerCase())));
    out.sort((a, b) => {
      const va = a[sort.key], vb = b[sort.key];
      return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir;
    });
    return out;
  }, [rows, search, tab, sort]);

  const th = (label, key, cls = '') => (
    <th className={`py-2 pr-2 cursor-pointer select-none ${cls}`} onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : -1 }))}>
      {label}{sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const del = async (r) => {
    if (!window.confirm(`Eliminare la promozione -${r.pct}% su ${r.property_name}?`)) return;
    try { await api.delete(`/promotions/${r.id}`); toast.success('Eliminata'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const cease = async (r) => {
    if (!window.confirm(`Cessare DA OGGI la promozione -${r.pct}% su ${r.property_name}?\n\nLe prenotazioni già arrivate mantengono lo sconto (il controllo pagamenti verifica fino alla data di cessazione). Da domani non si applica più ai nuovi arrivi.`)) return;
    try { await api.post(`/promotions/${r.id}/cease`); toast.success('Promozione cessata da oggi'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const sendSummary = async (r) => {
    try {
      const res = await api.post(`/promotions/${r.id}/send-summary`);
      res.data.sent ? toast.success('Riepilogo inviato a proprietario e partner') : toast.error('Invio non riuscito (email proprietario mancante?)');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const tot = useMemo(() => ({
    attive: rows.filter(r => r.status === 'attiva').length,
    nights: totals ? totals.nights : rows.reduce((s, r) => s + (r.nights_promo || 0), 0),
    owner: totals ? totals.owner_total : rows.reduce((s, r) => s + (r.owner_total || 0), 0),
    hw: totals ? totals.hw_collected_total : rows.reduce((s, r) => s + (r.hw_collected_total || 0), 0),
  }), [rows, totals]);

  return (
    <div className="p-6 space-y-5" data-testid="promozioni-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Promozioni</h1>
          <p className="text-sm text-slate-500">Scontistiche richieste dai proprietari, applicate su tutte le piattaforme. Una prenotazione è in promo se arriva nel periodo di validità e il soggiorno cade nelle date promozionate.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="promo-new-btn">
          <Plus className="h-4 w-4 mr-1" /> Crea nuova promozione
        </Button>
      </div>

      <div className={`grid ${isAdmin ? 'grid-cols-4 max-w-3xl' : 'grid-cols-3 max-w-xl'} gap-3`}>
        <Card className="p-3"><p className="text-xs text-slate-500">Promo attive</p><p className="text-xl font-bold text-emerald-600">{tot.attive}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Notti in promo</p><p className="text-xl font-bold">{tot.nights}</p></Card>
        <Card className="p-3"><p className="text-xs text-slate-500">Al proprietario</p><p className="text-xl font-bold">{eur(tot.owner)}</p></Card>
        {isAdmin && <Card className="p-3" data-testid="promo-stat-hw-collected"><p className="text-xs text-slate-500">Incassato HW <span className="text-[10px]">(incl. pulizie)</span></p><p className="text-xl font-bold text-emerald-700">{eur(tot.hw)}</p></Card>}
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-2 items-stretch gap-1 border-b mb-3 -mx-4 px-4 sm:flex sm:items-center">
          {[['piattaforme', 'Piattaforme'], ['da-attivare', 'Da attivare'], ['attive', 'Attive e future'], ['terminate', 'Terminate']].map(([k, label]) => {
            const n = k === 'da-attivare'
              ? (reqData?.requests || []).filter(r => ['pending', 'owner_blocked'].includes(r.status)).length
              : k === 'piattaforme' ? (taskData?.pending || 0)
              : rows.filter(r => (k === 'attive'
                ? !['conclusa', 'cessata'].includes(r.status)
                : ['conclusa', 'cessata'].includes(r.status))).length;
            return (
              <button key={k} onClick={() => setTab(k)} data-testid={`promo-tab-${k}`}
                className={`min-w-0 px-2 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors sm:px-4 ${tab === k
                  ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
                {label} <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === k ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{n}</span>
              </button>
            );
          })}
        </div>
        {tab === 'piattaforme' ? (<div className="space-y-5">
          <PromoPlatformTasks data={taskData} loading={tasksLoading} reload={loadTasks} />
          {isAdmin && <PromoOperationsAuditPanel onApplied={loadTasks} />}
        </div>) : tab === 'da-attivare' ? (
          <PartnerRequestsTable data={reqData} reload={loadReqs} onChanged={() => { load(); loadTasks(); }} />
        ) : (<>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca immobile…"
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="promo-search" />
          </div>
        </div>
        {loading ? <div className="py-12 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
          : !shown.length ? <p className="py-8 text-center text-sm text-slate-400">{tab === 'terminate' ? 'Nessuna promozione terminata.' : 'Nessuna promozione: creane una col pulsante in alto.'}</p>
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b text-xs">
                    {th('IMMOBILE', 'property_name')}{th('%', 'pct', 'text-center')}
                    {th('VALIDITÀ', 'validity_from')}{th('SOGGIORNI', 'stay_from')}
                    {th('CREATA IL', 'created_at')}{th('PREN.', 'bookings', 'text-center')}
                    {th('NOTTI', 'nights_promo', 'text-center')}{th('AL PROPRIETARIO', 'owner_total', 'text-right')}
                    {isAdmin && th('INCASSATO HW', 'hw_collected_total', 'text-right')}
                    {th('STATO', 'status', 'text-center')}<th></th>
                  </tr></thead>
                  <tbody>
                    {shown.map(r => (
                      <React.Fragment key={r.id}>
                        <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`promo-row-${r.property_code}`}
                          onClick={() => setOpen(open === r.id ? null : r.id)}>
                          <td className="py-2 pr-2"><span className="font-mono font-semibold">{r.property_code}</span> <span className="text-slate-500 text-xs">{(r.property_name || '').slice(0, 40)}</span>
                            {!r.notify && <span className="ml-1 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[10px]">senza email</span>}
                            {(() => { const o = ORIGIN_LABEL[r.origin || (r.partner_request_id ? 'partner_hwacademy' : '')]; return o ? <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${o[1]}`} data-testid={`promo-origin-badge-${r.property_code}`}>{o[0]}</span> : null; })()}</td>
                          <td className="py-2 pr-2 text-center font-bold text-blue-700">-{r.pct}%
                            {r.cumulative && <div className="text-[9px] font-semibold text-violet-600 bg-violet-50 rounded px-1 mt-0.5" data-testid={`promo-cumulative-badge-${r.property_code}`}>Σ SI SOMMA</div>}</td>
                          <td className="py-2 pr-2 text-xs">{fmtD(r.validity_from)} → {(r.validity_to || '').startsWith('9999') ? <span className="font-semibold text-emerald-700">∞ sempre</span> : fmtD(r.validity_to)}</td>
                          <td className="py-2 pr-2 text-xs">{fmtD(r.stay_from)} → {(r.stay_to || '').startsWith('9999') ? <span className="font-semibold text-emerald-700">∞ sempre</span> : fmtD(r.stay_to)}</td>
                          <td className="py-2 pr-2 text-xs">{fmtD(r.created_at)}</td>
                          <td className="py-2 pr-2 text-center">{r.bookings}</td>
                          <td className="py-2 pr-2 text-center">{r.nights_promo}</td>
                          <td className="py-2 pr-2 text-right font-semibold">{eur(r.owner_total)}</td>
                          {isAdmin && <td className="py-2 pr-2 text-right font-semibold text-emerald-700" data-testid={`promo-hw-total-${r.property_code}`}>{eur(r.hw_collected_total)}</td>}
                          <td className="py-2 pr-2 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_BADGE[r.status]}`}>{r.status.toUpperCase()}</span></td>
                          <td className="py-2 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                            {['attiva', 'futura'].includes(r.status) &&
                              <button title="Cessa da oggi" onClick={() => cease(r)} className="text-amber-600 mr-2" data-testid={`promo-cease-${r.property_code}`}><Ban className="h-4 w-4" /></button>}
                            {r.notify && ['conclusa', 'cessata'].includes(r.status) && !r.summary_sent_at &&
                              <button title="Invia riepilogo finale" onClick={() => sendSummary(r)} className="text-blue-600 mr-2" data-testid={`promo-summary-${r.property_code}`}><Send className="h-4 w-4" /></button>}
                            {isAdmin && <button title="Modifica" onClick={() => { setEditing(r); setFormOpen(true); }} className="text-slate-500 mr-2"><Pencil className="h-4 w-4" /></button>}
                            {isAdmin && <button title="Elimina" onClick={() => del(r)} className="text-red-500" data-testid={`promo-del-${r.property_code}`}><Trash2 className="h-4 w-4" /></button>}
                            <button title={open === r.id ? 'Chiudi prenotazioni' : 'Mostra prenotazioni'} data-testid={`promo-expand-${r.property_code}`}
                              onClick={() => setOpen(open === r.id ? null : r.id)} className="align-middle">
                              {open === r.id ? <ChevronUp className="h-4 w-4 inline text-slate-400 ml-1" /> : <ChevronDown className="h-4 w-4 inline text-slate-400 ml-1" />}
                            </button>
                          </td>
                        </tr>
                        {open === r.id && <tr><td colSpan={isAdmin ? 11 : 10}><PromoDetail promo={r} isAdmin={isAdmin} /></td></tr>}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </>)}
      </Card>
      <PromoForm open={formOpen} onClose={() => setFormOpen(false)} properties={props} isAdmin={isAdmin}
        editing={editing} onSaved={() => { load(); loadTasks(); }} />
    </div>
  );
}
